// §56 styled dialog controller: promise-based replacements for the native
// confirm()/prompt()/alert(). Extracted from the inline <script> that used to
// live in internal/ui/layout.templ so it rides tsc + esbuild like every other
// client module. The <dialog id="ui-dialog"> markup (with its i18n'd data-*-label
// attributes AND its Datastar data-on:* wiring) is server-rendered by
// `UIDialogHTML()`. Datastar owns the button/backdrop/close listeners
// declaratively -- this module registers NONE of its own. It only holds the
// in-flight call's state, configures the shared dialog's DOM per call, and
// exposes helpers on `window`: the public __confirm/__prompt/__notify/__choose
// (called from data-on:* expressions and the other bundles), plus the thin
// __dlgOk/__dlgPick/__dlgClosed that the dialog's own data-on:* call back into.

// Notice severity: recolors the eyebrow + severity icon so an error no longer
// reads like a neutral notice. Shares the callout tone vocabulary (info/warn/
// error/success) from the design system, so the dialog speaks the same severity
// language as the rest of the app. 'info' is the neutral default (green eyebrow,
// no icon); warn/error/success swap the eyebrow for a tinted sprite icon (the
// per-level colour comes from --confirm-accent in libretto.css).
type NoticeLevel = 'info' | 'warn' | 'error' | 'success';

// Sprite icon (in /static/icons.svg) shown before the message per level; 'info'
// shows none, keeping the default notice the clean editorial card it already is.
const NOTICE_ICON: Record<NoticeLevel, string> = {
  info: '',
  warn: 'i-triangle-alert',
  error: 'i-circle-alert',
  success: 'i-check-circle-2',
};

// A single selectable option in a __choose dialog: a titled, optionally-described
// button that resolves the dialog with its id.
interface Choice {
  id: string;
  label: string;
  desc?: string;
}

interface DialogOpts {
  msg?: string;
  prompt?: boolean; // show the text input; resolve its value (or null on cancel)
  notice?: boolean; // hide the cancel button (single-OK notice)
  danger?: boolean; // style OK as a destructive action (delete label + danger btn)
  def?: string; // default value for the prompt input
  level?: NoticeLevel; // notice severity (drives eyebrow colour + icon via CSS)
  choices?: Choice[]; // a pick-one list; each button resolves with its id (null on cancel)
}

const dlg = document.getElementById('ui-dialog') as HTMLDialogElement | null;
if (dlg) {
  const msgEl = document.getElementById('ui-dialog-msg') as HTMLElement;
  const input = document.getElementById('ui-dialog-input') as HTMLInputElement;
  const choicesEl = document.getElementById('ui-dialog-choices') as HTMLElement;
  const ok = document.getElementById('ui-dialog-ok') as HTMLButtonElement;
  const cancel = document.getElementById('ui-dialog-cancel') as HTMLButtonElement;
  const iconUse = document.querySelector('#ui-dialog-icon use') as SVGUseElement | null;
  cancel.textContent = dlg.dataset.cancelLabel ?? '';

  // The single in-flight call. `result` starts at the cancel default; OK/choice
  // overwrite it before closing. Datastar's data-on:close fires __dlgClosed once,
  // which reads this and resolves – so cancel/backdrop/Esc all resolve the cancel
  // default while OK/choice resolve what they recorded. Resolving only on close
  // (never synchronously in a click handler) means a chained `.then` that opens
  // the next dialog runs strictly after this one's close event, so the shared
  // dialog is never double-driven across calls.
  type Pending = { resolve: (v: string | boolean | null) => void; opts: DialogOpts; result: string | boolean | null };
  let current: Pending | null = null;

  // run configures the single shared dialog for one call. It attaches no
  // listeners: the markup's data-on:* (server-rendered in UIDialogHTML) drives
  // OK/cancel/backdrop/choice/close.
  const run = (opts: DialogOpts): Promise<string | boolean | null> =>
    new Promise((resolve) => {
      // A dialog already open means an overlapping call (rare – the modal normally
      // serializes callers). Resolve the previous with its pending result and
      // reconfigure the same open dialog rather than throwing on showModal().
      const alreadyOpen = current !== null;
      if (current) { const prev = current; current = null; prev.resolve(prev.result); }
      const cancelVal = opts.prompt || opts.choices ? null : false;
      current = { resolve, opts, result: cancelVal };
      msgEl.textContent = opts.msg ?? '';
      // Severity is a notice-only concern; confirm/prompt stay neutral ('info').
      // CSS keys the eyebrow colour off data-level; the sprite icon is set here.
      const level: NoticeLevel = (opts.notice && opts.level) || 'info';
      dlg.dataset.level = level;
      if (iconUse) {
        const icon = NOTICE_ICON[level];
        if (icon) iconUse.setAttribute('href', '/static/icons.svg#' + icon);
        else iconUse.removeAttribute('href');
      }
      input.style.display = opts.prompt ? '' : 'none';
      if (opts.prompt) input.value = opts.def ?? '';
      // Pick-one choices replace the OK button with a titled button list. Each
      // button carries its id in data-choice-id; the choices container's delegated
      // data-on:click reads it and resolves via __dlgPick.
      choicesEl.innerHTML = '';
      choicesEl.style.display = opts.choices ? '' : 'none';
      ok.style.display = opts.choices ? 'none' : '';
      cancel.style.display = opts.notice ? 'none' : '';
      ok.textContent = opts.danger ? (dlg.dataset.deleteLabel ?? '') : (dlg.dataset.okLabel ?? '');
      // A notice's OK is a plain dismiss (not an affirmative action), so it's a
      // neutral secondary button -- confirm/prompt keep the emphasised danger/
      // primary OK since there OK commits the destructive action or the input.
      ok.className = 'btn ' + (opts.danger ? 'btn--danger' : opts.notice ? 'btn--secondary' : 'btn--primary');
      for (const c of opts.choices ?? []) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'record-choice';
        b.dataset.choiceId = c.id; // read by the choices container's delegated data-on:click
        const t = document.createElement('span');
        t.className = 'record-choice__title';
        t.textContent = c.label;
        b.appendChild(t);
        if (c.desc) {
          const d = document.createElement('span');
          d.className = 'record-choice__desc';
          d.textContent = c.desc;
          b.appendChild(d);
        }
        choicesEl.appendChild(b);
      }
      if (!alreadyOpen) dlg.showModal();
      if (opts.prompt) { input.focus(); input.select(); }
    });

  // Datastar data-on:* → these three. OK/choice record the outcome then close the
  // dialog; the close drives __dlgClosed, the single resolve point.
  window.__dlgOk = () => { if (current) { current.result = current.opts.prompt ? input.value : true; dlg.close(); } };
  window.__dlgPick = (id) => { if (current) { current.result = id; dlg.close(); } };
  window.__dlgClosed = () => { if (!current) return; const c = current; current = null; c.resolve(c.result); };

  window.__confirm = (msg, danger) => run({ msg, danger: danger !== false }).then((v) => v === true);
  window.__prompt = (msg, def) => run({ msg, prompt: true, def: def ?? '' }).then((v) => v as string | null);
  window.__notify = (msg, level) => run({ msg, notice: true, level }).then(() => {});
  window.__choose = (msg, choices) => run({ msg, choices }).then((v) => (typeof v === 'string' ? v : null));
}

// Window-helper types (__confirm/__prompt/__notify/__choose) live in globals.d.ts.
export {};
